import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react'
import { List, Map as ImmutableMap, fromJS } from 'immutable'
import { useSelector, useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client'
import { useFeatureFlagVariantKey } from '@posthog/react'
import { compose } from 'recompose'
import { POSTHOG_FEATURE_FLAGS, EventContextProvider, usePostHogEvent } from '../PostHog/index'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import {
  AI_MODULE_VIEWED_GA4_EVENT,
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_ERROR_RESPONSE_GA4_EVENT,
  AI_RECOMMENDATION_RECEIVED_GA4_EVENT,
} from '../../services/event-tracking/index'
import {
  connect as connectPage,
  PAGE_SEO_TYPE_LOCATION,
} from '../Page/connect'
import { connect as connectStaticText } from '../StaticText/connect'
import { connect as connectTiles } from '../Tiles/connect'
import { TYPE_SEARCH } from '../../services/tiles/index'
import { STORE_KEY_SEARCH } from '../../services/store-keys/index'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { H5 } from '../Heading/index'
import TileV2Graphql from '../Tile.v2/Tile.v2Graphql/index'
import Row, { STYLES } from '../Row.v2/index'
import Button, { TYPES as BUTTON_TYPES } from '../Button/index'
import CommunityTabBar from '../Community/Identity/shared/CommunityTabBar/index'
import ProfileTileCard from '../Community/shared/ProfileTileCard/index'
import {
  createUpstreamContext,
  SCREEN_TYPE_SEARCH_PAGE,
} from '../../services/upstream-context/index'
import {
  buildTileData,
} from '../../services/community/identity/library/content-utils'
import type { OracleContentEntity } from '../../services/community/oracle/useOracleSearch'
import {
  CREATE_FOLLOW,
  DELETE_FOLLOW,
} from '../../services/community/query'
import type {
  FollowResponse,
  UnfollowResponse,
} from '../../services/community/types'
import useAiSearchStream from '../../services/community/oracle/useAiSearchStream'
import { PlaylistSearchTileV6 } from './PlaylistSearchTile'
import AiGuide from './AiGuide'
import SortBar from './SortBar'
import type { SortOption } from './SortBar'
import TraditionalResults from './TraditionalResults'
import type { SearchTab } from './types'

function withDefaultSortFromQuery (Component: React.ComponentType<Record<string, unknown>>) {
  return function WithDefaultSort (props: Record<string, unknown>) {
    const loc = props.location as { query?: { sort?: string } } | undefined
    const defaultSort = loc?.query?.sort || null
    return <Component {...props} defaultSort={defaultSort} />
  }
}

const EMPTY_TILES = ImmutableMap<string, unknown>()

// Extracted to module level to prevent new function references on every render
const selectSearchTilesState = (state: Record<string, unknown>): ImmutableMap<string, unknown> => {
  const tiles = state.tiles as ImmutableMap<string, unknown> | undefined
  if (!tiles) return EMPTY_TILES
  return tiles.get(STORE_KEY_SEARCH, EMPTY_TILES) as ImmutableMap<string, unknown>
}

const curatedUpstreamContext = createUpstreamContext({
  screenType: SCREEN_TYPE_SEARCH_PAGE,
  screenParam: 'ai-guide-search-curated',
  storeKey: 'ai-guide-search-curated',
})

interface ProfileTileItem {
  uid: string
  viewerContext: { isFollowing?: boolean | null } | null
}

interface AiGuideSearchPageProps {
  location: {
    query?: { q?: string; sort?: string }
    pathname?: string
    search?: string
  }
  staticText: ImmutableMap<string, unknown>
  history: Record<string, unknown>
  getTilesDataLoadMore?: (() => void) | null
}

function AiGuideSearchPage ({
  location,
  staticText,
  history,
  getTilesDataLoadMore = null,
}: AiGuideSearchPageProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<SearchTab>('videos')
  const [sortMode, setSortMode] = useState<SortOption>('curated')
  const [showClassic, setShowClassic] = useState(false)

  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt') as string | null
  const viewerUid = (auth.get('uid') as string | null | undefined) ?? null
  const language = (useSelector(selectPrimaryLanguage) || 'en') as string

  const q = location.query?.q || ''
  const dispatch = useDispatch()

  // ── PostHog + GA4 event tracking ────────────────────────────────
  const trackModuleViewed = usePostHogEvent('ai_module_viewed')
  const trackMessageSent = usePostHogEvent('ai_message_sent')
  const trackErrorResponse = usePostHogEvent('ai_error_response')

  // Fire ai_module_viewed on mount — triggers PostHog session recording
  useEffect(() => {
    trackModuleViewed('search_page')
    dispatch(setAiGa4Event(AI_MODULE_VIEWED_GA4_EVENT))
  }, [trackModuleViewed, dispatch])

  // Playlists + profiles tabs require yon-lauren-initial-test flag.
  // Users in ai-search-web experiment only see content + overview.
  const yonLaurenVariant = useFeatureFlagVariantKey(
    POSTHOG_FEATURE_FLAGS.COMMUNITY_YON_LAUREN_INITIAL_TEST,
  )
  const enablePlaylistsAndProfiles = yonLaurenVariant === 'email-matched'

  const {
    search: aiSearch,
    refine: aiRefine,
    loadMore: aiLoadMore,
    results: aiResults,
    streamingText,
    state: streamState,
    statusKey,
    error: aiError,
    sendFeedback,
    turnId,
    contentTotalCount,
    playlistTotalCount,
    profileTotalCount,
    activeQuery,
  } = useAiSearchStream(jwt, language, { enablePlaylistsAndProfiles })

  // Trigger AI search when the query value changes.
  // Note: do NOT add `location.key` here — SortBar pushes `?sort=...` URL
  // changes that bump location.key, and re-firing this effect on those
  // pushes both re-streams the AI search (looks like a page reload) and
  // overrides the user's just-clicked sort back to 'curated'.
  useEffect(() => {
    if (q) {
      aiSearch(q)
      setSortMode('curated')
      setShowClassic(false)
      trackMessageSent(q, 'Text', 'Text Field')
      dispatch(setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT))
    }
  }, [q, aiSearch, trackMessageSent, dispatch])

  // Track when content results arrive
  useEffect(() => {
    if (aiResults.content.length > 0) {
      dispatch(setAiGa4Event(AI_RECOMMENDATION_RECEIVED_GA4_EVENT))
    }
  }, [aiResults.content.length, dispatch])

  // Track search errors
  useEffect(() => {
    if (streamState === 'ERROR' && aiError) {
      trackErrorResponse(aiError, 'Text', 'Text Field')
      dispatch(setAiGa4Event(AI_ERROR_RESPONSE_GA4_EVENT))
    }
  }, [streamState, aiError, trackErrorResponse, dispatch])

  // REST tile state for non-curated sorts -- read from Redux
  const tilesState = useSelector(selectSearchTilesState)

  const tileData = tilesState.getIn(
    ['data', 'titles'],
    List(),
  ) as List<ImmutableMap<string, unknown>>
  const tilesProcessing = tilesState.get('processing', false) as boolean
  const tilesTotalCount = tilesState.getIn(['data', 'totalCount'], 0) as number

  // ── Curated video tiles from aiSearch results ───────────────────
  // Bridges Brooklyn's denormalized flat fields into the OracleContentEntity
  // shape that buildTileData expects, so TileV2Graphql renders full tiles
  // (play button, hover state, metadata tags) without federation.
  const curatedTileData = useMemo(() => {
    if (activeTab !== 'videos' || sortMode !== 'curated') {
      return List<ImmutableMap<string, unknown>>()
    }
    const built = aiResults.content
      .filter((r) => r.path)
      .map((r) => {
        const heroUrl = r.heroImageUrl || r.thumbnailUrl || ''
        const thumbUrl = r.thumbnailUrl || r.heroImageUrl || ''
        const entity = {
          id: r.contentId,
          contentId: r.contentId,
          contentType: r.contentType || 'video',
          title: r.title || '',
          path: r.path || '',
          instructor: r.instructor || null,
          displayType: r.displayType || null,
          subType: r.displayType || null,
          studio: r.studio || null,
          featureMedia: r.mediaId ? {
            mediaId: r.mediaId,
            duration: r.durationSeconds || 0,
            contentPolicy: { accessCheck: { allowed: true } },
          } : null,
          contentAssetReferences: thumbUrl
            ? [{ asset: { renderUrl: thumbUrl } }]
            : null,
          imageContentRelationships: [
            ...(heroUrl ? [{ type: 'd6-keyart-with-text', image: { url: heroUrl } }] : []),
            ...(thumbUrl ? [{ type: 'd6-keyart-no-text', image: { url: thumbUrl } }] : []),
          ],
          episodeNumber: r.episodeNumber || null,
          seasonNumber: r.seasonNumber || null,
          contentSeries: r.seriesTitle
            ? [{ title: r.seriesTitle, path: '', contentId: r.seriesContentId || 0 }]
            : null,
          references: (r.contentReferences || []).map((ref) => ({
            contentReferenceType: { type: ref.type },
            contentTo: [{
              title: ref.name, contentType: '', contentId: 0, path: '', language,
            }],
          })),
        }
        return buildTileData(entity as unknown as OracleContentEntity)
      })
      .filter((tile): tile is NonNullable<typeof tile> => !!tile)
    return List(
      built.map((tile) => fromJS(tile)),
    ) as List<ImmutableMap<string, unknown>>
  }, [aiResults.content, activeTab, sortMode])

  const hasMoreCurated = curatedTileData.size < contentTotalCount

  // ── Profile tile data ──────────────────────────────────────────
  // The AI search response carries minimal profile data; we wrap each
  // result's `uid` + `viewerContext` in an Immutable Map so Row.v2
  // accepts them. ProfileTileCard fetches the full profile via
  // `useProfileByUid` once mounted in the grid slot.
  //
  // TODO(backend): The AI search response currently includes the
  // viewer's own profile in the results. Until the search API filters
  // it out server-side, drop the entry whose uid matches the logged-in
  // viewer (`auth.uid`) here so users don't see themselves in their own
  // search. We can't rely on `viewerContext.isOwner` — the AI search
  // response evidently doesn't populate it. Filtering by uid still
  // shows every other result; only the viewer's own card is removed.
  // The tab-count chip still reflects the server-reported total
  // (`profileTotalCount`); off-by-one when the owner appears is
  // acceptable as a temporary client-side patch.
  // Normalize viewerUid for comparison — `auth.get('uid')` is a number
  // while the AI search result's `profile.uid` is a string.
  // CommunityProfilePage uses the same `String(...) === String(...)`
  // coercion when computing ownership, so we mirror that here.
  // The viewer's own profile is NOT filtered out of results — instead
  // ProfileTileCard hides its Follow/Share actions when `isViewer` is
  // true so the card is still visible but inactive for self.
  const normalizedViewerUid = viewerUid != null ? String(viewerUid) : null

  // Plain JS array — `Row.v2` is no longer the renderer for the
  // profiles tab (we use a CSS Grid container instead, see the JSX
  // below), so the Immutable wrapping that Row required is gone.
  const profileTileItems = useMemo<ProfileTileItem[]>(() => {
    if (activeTab !== 'profiles') {
      return []
    }
    return aiResults.profiles
      .filter((result) => !!result.profile)
      .map((result) => ({
        uid: String(result.profile?.uid ?? ''),
        viewerContext: result.profile?.viewerContext ?? null,
      }))
  }, [aiResults.profiles, activeTab])

  // ── Profile follow / unfollow ──────────────────────────────────
  // Wraps userProfileFollow / userProfileUnfollow the same way ConnectionsModal
  // does. AI search is the first user-facing surface to activate these.
  const [followProfile] = useMutation<FollowResponse>(CREATE_FOLLOW, {
    context: { jwt },
  })
  const [unfollowProfile] = useMutation<UnfollowResponse>(DELETE_FOLLOW, {
    context: { jwt },
  })

  // Local follow-toggle overrides take precedence over the server-supplied
  // viewerContext.isFollowing. This protects optimistic toggles from being
  // resurrected by stale viewerContext on a subsequent aiResults refetch.
  const [followOverrides, setFollowOverrides] = useState<Map<string, boolean>>(
    () => new Map(),
  )

  const isProfileFollowing = useCallback(
    (uid: string, vc?: { isFollowing?: boolean | null } | null): boolean => (
      followOverrides.has(uid)
        ? !!followOverrides.get(uid)
        : !!vc?.isFollowing
    ),
    [followOverrides],
  )

  const handleProfileFollowToggle = useCallback(async (
    uid: string,
    currentlyFollowing: boolean,
  ) => {
    const nextFollowing = !currentlyFollowing
    setFollowOverrides((prev) => {
      const next = new Map(prev)
      next.set(uid, nextFollowing)
      return next
    })
    try {
      if (currentlyFollowing) {
        await unfollowProfile({ variables: { uid } })
      } else {
        await followProfile({ variables: { uid } })
      }
    } catch {
      setFollowOverrides((prev) => {
        const next = new Map(prev)
        next.set(uid, currentlyFollowing)
        return next
      })
    }
  }, [followProfile, unfollowProfile])

  // ── Static text ────────────────────────────────────────────────
  const searchResultsFor = staticText.getIn(
    ['data', 'searchResultsFor'],
    'Search results for "{query}"',
  ) as string
  const viewClassicResultsLabel = staticText.getIn(
    ['data', 'viewClassicResults'],
    'View classic search results',
  ) as string
  const loadMoreLabel = staticText.getIn(
    ['data', 'loadMore'],
    'Load More',
  ) as string
  const noVideoResultsLabel = staticText.getIn(
    ['data', 'noVideoResults'],
    'No video results found.',
  ) as string
  const noPlaylistResultsLabel = staticText.getIn(
    ['data', 'noPlaylistResults'],
    'No playlist results found.',
  ) as string
  const noProfileResultsLabel = staticText.getIn(
    ['data', 'noProfileResults'],
    'No profile results found.',
  ) as string
  const handleSortChange = useCallback((sort: SortOption) => {
    setSortMode(sort)
    setShowClassic(false)
  }, [])

  const isLoading = streamState === 'LOADING'
  const isStreaming = streamState === 'STREAMING'

  // Tab count chip state — `undefined` hides the chip (no query yet),
  // `null` renders the layout placeholder while the request is in flight,
  // `number` renders the count once the AI search response lands.
  const hasQuery = Boolean(q || activeQuery)
  const tabCount = (n: number): number | null | undefined => {
    if (!hasQuery) return undefined
    if (isLoading) return null
    return n
  }

  // Videos chip reflects what's actually rendered in the tile grid below
  // (not the AI/REST reported total) so the number matches the user's
  // mental model after Load More clicks and sort-filter switches. Curated
  // sort feeds the grid from aiResults.content; every other sort feeds it
  // from the REST tiles store.
  const displayedVideoCount = sortMode === 'curated'
    ? aiResults.content.length
    : tileData.size

  // For non-curated sorts, mirror AI-loading behavior: while the REST fetch
  // is in-flight with no results yet, show the layout placeholder instead
  // of flashing "0".
  const videosTabCount = sortMode !== 'curated'
      && hasQuery
      && tilesProcessing
      && tileData.size === 0
    ? null
    : tabCount(displayedVideoCount)
  const playlistsTabCount = tabCount(playlistTotalCount)
  const profilesTabCount = tabCount(profileTotalCount)

  const videosTabLabel = staticText.getIn(['data', 'videos'], 'Videos') as string
  const playlistsTabLabel = staticText.getIn(['data', 'playlists'], 'Playlists') as string
  const profilesTabLabel = staticText.getIn(['data', 'profiles'], 'Profiles') as string

  const tabs = useMemo(() => {
    const t = [
      { key: 'videos' as SearchTab, label: videosTabLabel, count: videosTabCount },
    ]
    if (enablePlaylistsAndProfiles) {
      t.push(
        { key: 'playlists' as SearchTab, label: playlistsTabLabel, count: playlistsTabCount },
        { key: 'profiles' as SearchTab, label: profilesTabLabel, count: profilesTabCount },
      )
    }
    return t
  }, [
    videosTabLabel,
    playlistsTabLabel,
    profilesTabLabel,
    videosTabCount,
    playlistsTabCount,
    profilesTabCount,
    enablePlaylistsAndProfiles,
  ])

  return (
    <EventContextProvider context="ai_search">
    <div className="ai-guide-search">
      <AiGuide
        staticText={staticText}
        streamingText={streamingText}
        loading={isLoading}
        isStreaming={isStreaming}
        statusKey={statusKey}
        error={aiError}
        refine={aiRefine}
        sendFeedback={sendFeedback}
        turnId={turnId}
      />

      <div className="ai-guide-search__controls">
        {enablePlaylistsAndProfiles && (
          <CommunityTabBar<SearchTab>
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="ai-guide-search__tab-bar"
          />
        )}
        {activeTab === 'videos' ? (
          <SortBar
            staticText={staticText}
            history={history}
            location={location}
            auth={auth}
            language={language}
            sortMode={sortMode}
            onSortChange={handleSortChange}
          />
        ) : null}
      </div>

      {q ? (
        <div className="ai-guide-search__results-bar">
          <H5 className="ai-guide-search__results-heading">
            {searchResultsFor.replace('{query}', activeQuery || q)}
          </H5>
        </div>
      ) : null}

      {/* Videos tab -- Curated (aiSearch) */}
      {activeTab === 'videos' && sortMode === 'curated' && (
        <>
          {(isLoading || isStreaming) && curatedTileData.size === 0 && (
            <div className="ai-guide-search__skeleton-tiles">
              {Array.from({ length: 16 }, (_, i) => (
                <div key={i} className="ai-guide-search__skeleton-tile">
                  <div className="ai-guide-search__skeleton-tile-hero" />
                </div>
              ))}
            </div>
          )}
          {curatedTileData.size > 0 && (
            <div className="tile-grid-v2">
              <Row
                items={curatedTileData}
                createAccessor={() => null}
                style={STYLES.S12345}
                asGrid
              >
                {(item: ImmutableMap<string, unknown>, params: { index: number }) => (
                  <TileV2Graphql
                    key={String(item.get('id', params.index))}
                    data={item || ImmutableMap()}
                    sectionIndex={0}
                    rowPlacement={0}
                    adminTitle="AI Curated Results"
                    index={params.index}
                    upstreamContext={curatedUpstreamContext}
                  />
                )}
              </Row>
            </div>
          )}
          {!isLoading && !isStreaming && curatedTileData.size === 0 && q && (
            <p className="ai-guide-search__ai-empty">{noVideoResultsLabel}</p>
          )}
        </>
      )}

      {/* Videos tab -- Non-curated sorts (REST) */}
      {activeTab === 'videos' && sortMode !== 'curated' && (
        <TraditionalResults
          tileData={tileData}
          loading={tilesProcessing}
          totalCount={tilesTotalCount}
          loadMore={getTilesDataLoadMore}
          q={q}
          staticText={staticText}
        />
      )}

      {/* Playlists tab — fluid grid of V6 (Golden Spiral) tiles fed
          from `aiResults.playlists`. Same auto-fill + minmax(230px, 1fr)
          grid as the Profiles tab so card widths track viewport size
          uniformly across tabs. */}
      {activeTab === 'playlists' && (
        <>
          {(isLoading || isStreaming) && aiResults.playlists.length === 0 && (
            <div className="ai-guide-search__skeleton-tiles">
              {Array.from({ length: 16 }, (_, i) => (
                <div key={i} className="ai-guide-search__skeleton-tile">
                  <div className="ai-guide-search__skeleton-tile-hero" />
                </div>
              ))}
            </div>
          )}
          {aiResults.playlists.length > 0 && (
            <div className="ai-guide-search__playlist-grid">
              {aiResults.playlists.map((result) => (
                <PlaylistSearchTileV6
                  key={result.playlistId}
                  playlistId={result.playlistId}
                  playlistName={result.playlistName}
                  itemCount={result.itemCount}
                  previewThumbnailUrls={result.previewThumbnailUrls}
                  previewContentTitles={result.previewContentTitles}
                  creatorDisplayName={result.creatorDisplayName}
                />
              ))}
            </div>
          )}
          {!isLoading && !isStreaming && aiResults.playlists.length === 0 && q && (
            <p className="ai-guide-search__ai-empty">{noPlaylistResultsLabel}</p>
          )}
        </>
      )}

      {/* Profiles tab -- fluid CSS Grid (auto-fill + minmax(230px, 1fr)).
          Drops `Row.v2` for this tab so card width tracks viewport
          smoothly from 375 → 1440px instead of step-changing at the
          5 fixed S12345 breakpoints. Each card fills its grid cell
          (ProfileTileCard sets width: 100% / height: 280px). */}
      {activeTab === 'profiles' && (
        <>
          {profileTileItems.length > 0 && (
            <div className="ai-guide-search__profile-grid">
              {profileTileItems.map((item) => {
                const isViewer = !!normalizedViewerUid
                  && item.uid === normalizedViewerUid
                return (
                  <ProfileTileCard
                    key={item.uid}
                    uid={item.uid}
                    language={language}
                    jwt={jwt}
                    isViewer={isViewer}
                    isFollowing={isProfileFollowing(item.uid, item.viewerContext)}
                    onFollowToggle={handleProfileFollowToggle}
                  />
                )
              })}
            </div>
          )}
          {!isLoading && !isStreaming && aiResults.profiles.length === 0 && q && (
            <p className="ai-guide-search__ai-empty">{noProfileResultsLabel}</p>
          )}
        </>
      )}

      {/* Load More for curated content */}
      {activeTab === 'videos' && sortMode === 'curated' && hasMoreCurated && curatedTileData.size > 0 && (
        <div className="ai-guide-search__load-more">
          <Button
            type={BUTTON_TYPES.GHOST}
            onClick={aiLoadMore}
            disabled={isLoading}
          >
            {loadMoreLabel}
          </Button>
        </div>
      )}

      {/* Classic results toggle link */}
      {!showClassic && sortMode === 'curated' && q && activeTab === 'videos' && (
        <p className="ai-guide-search__classic-link">
          <a
            href="#"
            role="button"
            onClick={(e) => {
              e.preventDefault()
              setShowClassic(true)
            }}
          >
            {viewClassicResultsLabel}
          </a>
        </p>
      )}

      {/* Classic (traditional) results when toggled */}
      {showClassic && activeTab === 'videos' && (
        <TraditionalResults
          tileData={tileData}
          loading={tilesProcessing}
          totalCount={tilesTotalCount}
          loadMore={getTilesDataLoadMore}
          q={q}
          staticText={staticText}
        />
      )}
    </div>
    </EventContextProvider>
  )
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION, storeBranch: '', storeKey: '' }),
  connectStaticText({ storeKey: 'aiGuideSearchPage' }),
  withDefaultSortFromQuery,
  connectTiles({
    storeKey: STORE_KEY_SEARCH,
    type: TYPE_SEARCH,
    queryParamAsId: 'q',
    ignoreEmptyId: true,
    enablePageBehaviors: true,
  }),
)(AiGuideSearchPage)
